import { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Facebook, Linkedin, Instagram, Youtube, Twitter, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

export default function Footer() {
  const [logoError, setLogoError] = useState(false);
  const { t, language } = useLanguage();
  const { theme } = useTheme();

  const logoSrc = theme === 'dark' ? '/logo.png' : '/TREID - Logotipo Negro.svg';

  const socialLinks = [
    { icon: Facebook, href: "https://www.facebook.com/treid.slp/", label: "Facebook" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/74748660/admin/dashboard/", label: "LinkedIn" },
    { icon: Instagram, href: "https://www.instagram.com/treid.comext/", label: "Instagram" },
    { icon: Youtube, href: "https://www.youtube.com/channel/UCJOKoHSbFDwNRwAJryUwGMw", label: "YouTube" },
    { icon: Twitter, href: "https://x.com/TREIDCOMEXT", label: "X (Twitter)" }
  ];

  const isDark = theme === 'dark';

  return (
    <footer className={`w-full py-16 px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 ${isDark ? 'bg-[#0c0b0b] border-zinc-900 text-zinc-400' : 'bg-[#faf9f6] border-zinc-200 text-zinc-600'} border-t relative z-10 transition-colors duration-300`}>
      <div className="space-y-6">
        <div>
          <Link to="/" className="flex items-center">
            {!logoError ? (
              <img 
                src={logoSrc} 
                alt="TREID" 
                width="160"
                height="40"
                className={`w-auto object-contain transition-all -translate-x-1 h-[42px] md:h-[50.4px]`} 
                onError={() => setLogoError(true)} 
              />
            ) : (
              <div className="text-2xl font-bold tracking-tighter flex items-center">
                <span className="text-primary mr-1">●</span> 
                <span className={`font-hanken uppercase italic ${isDark ? 'text-white' : 'text-zinc-900'}`}>TREID</span>
              </div>
            )}
          </Link>
        </div>
        <p className={`font-body-sm max-w-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
          {t('footer.description')}
        </p>

        <div className="pt-1 pb-1">
          <a 
            href="https://share.google/kJRzFHLII8GD9pfBK"
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-all ${isDark ? 'text-zinc-400 hover:text-primary' : 'text-zinc-600 hover:text-primary'}`}
            title={language === 'es' ? 'Ver dirección en Google Maps' : 'View address on Google Maps'}
          >
            <MapPin size={14} className="text-primary shrink-0 animate-pulse" />
            <span>
              {language === 'es' ? 'Sede Central: San Luis Potosí, México' : 'Headquarters: San Luis Potosi, Mexico'}
            </span>
          </a>
        </div>
        
        {/* Social Media Links */}
        <div className="flex items-center gap-4 py-2">
          {socialLinks.map((social) => {
            const IconComponent = social.icon;
            return (
              <a 
                key={social.label} 
                href={social.href} 
                target="_blank" 
                rel="noreferrer"
                aria-label={social.label}
                className={`transition-all p-2 rounded-full border border-transparent hover:border-primary/20 ${isDark ? 'text-zinc-400 hover:text-primary bg-zinc-900/50 hover:shadow-[0_0_12px_rgba(254,59,31,0.2)]' : 'text-zinc-600 hover:text-primary bg-zinc-200/50 hover:bg-zinc-200'}`}
              >
                <IconComponent size={18} />
              </a>
            );
          })}
        </div>

        <p className={`font-hanken text-[10px] uppercase tracking-widest ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
          © 2024 TREID Digital Ecosystem. {t('nav.intelligence')}. {t('footer.rights')}
        </p>
      </div>
      <div className="flex flex-col md:items-end justify-between">
        <div className="flex flex-wrap gap-8 mb-8 md:mb-0">
          {[t('footer.solutions'), t('footer.privacy'), t('footer.terms'), t('footer.network'), t('footer.newsletter')].map((link) => (
            <a key={link} href="#" className={`label-caps transition-colors text-[10px] ${isDark ? 'text-zinc-300 hover:text-primary' : 'text-zinc-800 hover:text-primary'}`}>
              {link}
            </a>
          ))}
        </div>
        <p className={`text-[11px] font-hanken tracking-wider ${isDark ? 'text-zinc-300' : 'text-zinc-800'}`}>
          MARKETING Y DISEÑO WEB POR VITAPYME
        </p>
      </div>
    </footer>
  );
}

